import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import * as React from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useCurrentSite } from '@hotjar/site';
import type { SiteId } from '@hotjar/site';
import type { Settings as EmailSettings } from '@hotjar/webapp/features/Forwarding/ForwardToEmail';
import { ForwardToEmail } from '@hotjar/webapp/features/Forwarding/ForwardToEmail';
import { ForwardToMsTeams } from '@hotjar/webapp/features/Forwarding/ForwardToMsTeams';
import { ForwardToSlackConnected as ForwardToSlack } from '@hotjar/webapp/features/Forwarding/ForwardToSlackConnected';
import { ForwardToWebhooks } from '@hotjar/webapp/features/Forwarding/ForwardToWebhooks';
import type { Settings as SlackSettings } from '@hotjar/webapp/features/Forwarding/ForwardToSlack';
import type { MsTeamsFeedbackSettings as MsTeamsSettings } from '@hotjar/integrations';
import { WebhooksFeature } from '@hotjar/integrations';
import type { WebhooksSettings } from '@hotjar/integrations/domains/webhooks/api';
import { ForwardToZapier } from '@hotjar/webapp/features/Forwarding/ForwardToZapier';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { ErrorBoundary } from '@hotjar/error-handler';

const ForwardFeedbackContainer = styled.section`
  max-width: 520px;
  margin: 0 auto;
`;

const OptionWrapper = styled.section`
  padding: ${tokens.spacer.x3} 0;
`;

const Hr = styled.hr`
  margin: 0 !important;
`;

interface ForwardFeedbackProps {
  slack?: SlackSettings | null;
  email?: EmailSettings | null;
  msTeams?: MsTeamsSettings | null;
  webhooks?: WebhooksSettings | null;
  zapier?: boolean;
  onChange: (settings: {
    slack?: SlackSettings;
    email?: EmailSettings;
    msTeams?: Partial<MsTeamsSettings>;
    webhooks?: Partial<WebhooksSettings>;
  }) => void;
  siteId: SiteId;
  feedbackId: number | null;
}

export const ForwardFeedback = ({
  slack = null,
  email = null,
  msTeams = null,
  webhooks = null,
  zapier = true,
  onChange,
  siteId,
  feedbackId,
}: ForwardFeedbackProps): JSX.Element | null => {
  const onSlackChange = useCallback(
    (slackSettings) => onChange({ slack: slackSettings }),
    [onChange],
  );
  const onEmailChange = useCallback(
    (emailSettings) => onChange({ email: emailSettings }),
    [onChange],
  );
  const onMsTeamsChange = useCallback(
    (msTeamsSettings: Partial<MsTeamsSettings>) => onChange({ msTeams: msTeamsSettings }),
    [onChange],
  );
  const onWebhooksChange = useCallback(
    (settings: Partial<WebhooksSettings>) => onChange({ webhooks: settings }),
    [onChange],
  );

  const { t } = useTranslation();
  const { data: site } = useCurrentSite();

  const showWebhooks = webhooks && WebhooksFeature.canUse(site);

  if (!slack && !email && !msTeams && !showWebhooks) {
    return null;
  }

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <ForwardFeedbackContainer>
        {slack && (
          <OptionWrapper>
            <ForwardToSlack
              trackingState="Forward Feedback Widget"
              onChange={onSlackChange}
              settings={slack}
              contextOfUse={ProductType.Feedback}
              header={t('common:incoming-feedback.creation.forwarding.slack.title')}
              subtext={t('common:incoming-feedback.creation.forwarding.slack.subtext')}
            />
          </OptionWrapper>
        )}
        {msTeams && (
          <>
            <Hr />
            <OptionWrapper>
              <ForwardToMsTeams
                trackingState="Forward Feedback Widget"
                onChange={onMsTeamsChange}
                settings={msTeams}
                contextOfUse={ProductType.Feedback}
                header={t('common:incoming-feedback.creation.forwarding.ms-teams.title')}
              />
            </OptionWrapper>
          </>
        )}
        {showWebhooks && (
          <>
            <Hr />
            <OptionWrapper>
              <ForwardToWebhooks
                trackingState="Forward Feedback Widget"
                onChange={onWebhooksChange}
                settings={webhooks}
                contextOfUse={ProductType.Feedback}
                header={t('common:incoming-feedback.creation.forwarding.webhooks.title')}
              />
            </OptionWrapper>
          </>
        )}
        {email && (
          <>
            <Hr />
            <OptionWrapper>
              <ForwardToEmail
                trackingState="Forward Feedback Widget"
                onChange={onEmailChange}
                settings={email}
                header={t('common:incoming-feedback.creation.forwarding.email.title')}
                subtext={t('common:incoming-feedback.creation.forwarding.email.subtext')}
              />
            </OptionWrapper>
          </>
        )}
        {zapier && (
          <>
            <Hr />
            <OptionWrapper>
              <ForwardToZapier
                trackingState="Forward Feedback Widget"
                siteId={siteId}
                widgetId={feedbackId}
                widgetType="incoming_feedback"
                disabledTooltip={t('common:incoming-feedback.creation.forwarding.zapier.disabled')}
                title={t('common:incoming-feedback.creation.forwarding.zapier.title')}
                contextOfUse={ProductType.Feedback}
              />
            </OptionWrapper>
          </>
        )}
      </ForwardFeedbackContainer>
    </ErrorBoundary>
  );
};
